import {
    DocumentJobStatus,
    type BoxTruckAssetDocType,
    type BoxTruckDocType,
    type BoxTruckOwnerDocType,
    type CdlFacts,
    type DocumentImmediateResult,
    type VehicleInsuranceFacts,
    type VehicleRegistrationFacts,
    type VoidedCheckFacts,
    type W9Facts,
} from '@numeo/types-core'

/** A value a document can put in a form field. */
export type AutofillValue = string | number

/** The form keys each profile form fills from a document: the driver draft's keys, the truck's dotted paths, the contractor's. */
export type DriverAutofillField =
    | 'name'
    | 'licenseType'
    | 'licenseNumber'
    | 'licenseState'
    | 'licenseExpirationDate'
    | 'birthDate'
    | 'bankName'
    | 'bankAccountNumber'
    | 'bankRoutingNumber'
    // The driver record's address: no card reads it yet, the drivers-list CDL scan fills it (`cdlScanAutofillMarks`).
    | 'address'
    | 'city'
    | 'state'
    | 'zipCode'
export type VehicleAutofillField =
    | 'vin'
    | 'make'
    | 'year'
    | 'boxTruck.registrationPlate'
    | 'boxTruck.payloadLbs'
    | 'boxTruck.gvwLbs'
    | 'boxTruck.expirationDate'
    | 'boxTruck.registrationState'
    | 'boxTruck.insuranceCompany'
    | 'boxTruck.insuranceExpirationDate'
    | 'boxTruck.insurancePolicyNumber'
export type OwnerAutofillField = 'companyDetails.name' | 'companyDetails.fein'

/** The values to write, field by field, keys kept typed. */
export function autofillEntries<K extends string>(values: Partial<Record<K, AutofillValue | null>>): Array<{ field: K; value: AutofillValue | null }> {
    const entries: Array<{ field: K; value: AutofillValue | null }> = []
    for (const field in values) {
        const value = values[field]
        if (value !== undefined) entries.push({ field, value })
    }
    return entries
}

/** How one document card is read and what its facts fill. */
export interface AutofillTarget<K extends string> {
    /** The pipeline's doc type for the card (ADR-022.2). */
    docTypeHint: string
    /** The document's name in a sentence: "This doesn't look like a {label}." */
    label: string
    /** The fields it can fill, by the labels the form shows them under: what the card's AI button tells HR it fills. */
    fills: readonly string[]
    /**
     * The field at the top of the form's block that holds most of what it fills (the Driver License fields for a license, not the
     * Name above them): where a read with nothing to decide brings HR.
     */
    anchor: K
    toFields: (facts: Record<string, unknown>) => Partial<Record<K, AutofillValue>>
}

/** A string fact, by a key of the type's output contract in `@numeo/types`: a field renamed on the server fails to compile here. */
const text = <F>(facts: Record<string, unknown>, key: keyof F & string): string | undefined => {
    const value = facts[key]
    return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

const amount = <F>(facts: Record<string, unknown>, key: keyof F & string): number | undefined => {
    const value = facts[key]
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/** The fields that were read, without the ones the document left empty: an empty read never clears a field. */
const present = <K extends string>(fields: Record<K, AutofillValue | undefined>): Partial<Record<K, AutofillValue>> => {
    const out: Partial<Record<K, AutofillValue>> = {}
    for (const key in fields) {
        const value = fields[key]
        if (value !== undefined) out[key] = value
    }
    return out
}

/** Keyed by the card's document type in `@numeo/types`: a card renamed there fails to compile here instead of losing autofill. */
export const DRIVER_AUTOFILL_TARGETS: Partial<Record<BoxTruckDocType, AutofillTarget<DriverAutofillField>>> = {
    driver_license: {
        docTypeHint: 'cdl',
        label: 'driver license',
        anchor: 'licenseType',
        fills: ['Name', 'License Type', 'License Number', 'License State', 'License Expiration', 'Birth Date'],
        toFields: (facts) =>
            present({
                // Given names first ("ISAEL DIAZ PADRON"): the reader builds it from the card's two name lines.
                name: text<CdlFacts>(facts, 'fullName'),
                licenseType: text<CdlFacts>(facts, 'licenseType'),
                licenseNumber: text<CdlFacts>(facts, 'cdlNumber'),
                licenseState: text<CdlFacts>(facts, 'licenseState'),
                licenseExpirationDate: text<CdlFacts>(facts, 'expiryDate'),
                birthDate: text<CdlFacts>(facts, 'dateOfBirth'),
            }),
    },
    voided_check: {
        docTypeHint: 'voided_check',
        label: 'voided check or bank letter',
        anchor: 'bankName',
        fills: ['Bank Name', 'Account Number', 'Routing Number'],
        toFields: (facts) =>
            present({
                bankName: text<VoidedCheckFacts>(facts, 'bankName'),
                bankAccountNumber: text<VoidedCheckFacts>(facts, 'accountNumber'),
                bankRoutingNumber: text<VoidedCheckFacts>(facts, 'routingNumber'),
            }),
    },
}

export const VEHICLE_AUTOFILL_TARGETS: Partial<Record<BoxTruckAssetDocType, AutofillTarget<VehicleAutofillField>>> = {
    vehicle_registration: {
        docTypeHint: 'vehicle_registration',
        label: 'vehicle registration',
        anchor: 'make',
        fills: ['VIN', 'Make', 'Vehicle Year', 'Payload', 'GVW', 'Registration Plate', 'Registration Expiry', 'Registration State'],
        toFields: (facts) =>
            present({
                vin: text<VehicleRegistrationFacts>(facts, 'vin'),
                make: text<VehicleRegistrationFacts>(facts, 'make'),
                year: amount<VehicleRegistrationFacts>(facts, 'year'),
                'boxTruck.registrationPlate': text<VehicleRegistrationFacts>(facts, 'registrationPlate'),
                'boxTruck.payloadLbs': amount<VehicleRegistrationFacts>(facts, 'payloadLbs'),
                'boxTruck.gvwLbs': amount<VehicleRegistrationFacts>(facts, 'gvwLbs'),
                'boxTruck.expirationDate': text<VehicleRegistrationFacts>(facts, 'registrationExpirationDate'),
                'boxTruck.registrationState': text<VehicleRegistrationFacts>(facts, 'registrationState'),
            }),
    },
    vehicle_insurance: {
        docTypeHint: 'vehicle_insurance',
        label: 'insurance card or certificate',
        anchor: 'boxTruck.insuranceCompany',
        fills: ['Insurance Company', 'Insurance Expiry', 'Policy #'],
        toFields: (facts) =>
            present({
                'boxTruck.insuranceCompany': text<VehicleInsuranceFacts>(facts, 'insuranceCompany'),
                'boxTruck.insuranceExpirationDate': text<VehicleInsuranceFacts>(facts, 'insuranceExpirationDate'),
                'boxTruck.insurancePolicyNumber': text<VehicleInsuranceFacts>(facts, 'policyNumber'),
            }),
    },
}

export const OWNER_AUTOFILL_TARGETS: Partial<Record<BoxTruckOwnerDocType, AutofillTarget<OwnerAutofillField>>> = {
    w9: {
        docTypeHint: 'w9',
        label: 'W-9 or IRS EIN letter',
        anchor: 'companyDetails.name',
        fills: ['Company Name', 'EIN'],
        // The model picks the company from W-9 lines 1 and 2 (`companyName`); an individual with no business name fills none.
        toFields: (facts) => present({ 'companyDetails.name': text<W9Facts>(facts, 'companyName'), 'companyDetails.fein': text<W9Facts>(facts, 'ein') }),
    },
}

/** The card that reads a document type the pipeline answered with (`cdl` → `driver_license`), when a profile card reads it. */
export function cardOfDocType(docType: string): string | undefined {
    return readerOf(docType)?.card
}

/** The name in a sentence of a document type the pipeline answered with, when a profile card reads that type. */
export function documentTypeLabel(docType: string | undefined): string | undefined {
    return docType === undefined ? undefined : readerOf(docType)?.label
}

/** The profile card that reads a pipeline doc type, with the document's name in a sentence: the one search both lookups use. */
function readerOf(docType: string): { card: string; label: string } | undefined {
    const forms: ReadonlyArray<Partial<Record<string, { docTypeHint: string; label: string }>>> = [
        DRIVER_AUTOFILL_TARGETS,
        VEHICLE_AUTOFILL_TARGETS,
        OWNER_AUTOFILL_TARGETS,
    ]
    for (const targets of forms) {
        for (const [card, target] of Object.entries(targets)) if (target?.docTypeHint === docType) return { card, label: target.label }
    }
    return undefined
}

/** Why a file filled nothing: what a read ended as when it gave no facts for the card. The one list, used by the card and by analytics. */
export type AutofillProblem = 'wrong_document' | 'several_documents' | 'unreadable' | 'rejected' | 'gone' | 'failed' | 'timeout'

/** Reads of one file after its first: a document that fails three times will not be read by a fourth. */
export const MAX_AUTOFILL_RETRIES = 2

/** A problem Try again can fix: the read failed or ran out of time. Every other one is what the file is, and reads the same again. */
export const isRetryableProblem = (problem: AutofillProblem | 'facts'): boolean => problem === 'failed' || problem === 'timeout'

/** Whether a read that ended in `problem`, after `retries` tries again, may be tried again. The one rule the card and the engine use. */
export const canTryAgain = (problem: AutofillProblem | 'facts', retries: number): boolean => isRetryableProblem(problem) && retries < MAX_AUTOFILL_RETRIES

/** What one read means for the card. A wrong document says what it was read as, and whether that answer is an earlier read of the same bytes. */
type ReadOutcome =
    | { kind: 'facts'; facts: Record<string, unknown> }
    | { kind: 'wrong_document'; readAs?: string; cached: boolean }
    | { kind: 'several_documents' }
    | { kind: 'unreadable' }
    | { kind: 'failed' }

/** A finished job as the card's outcome: facts only when the document was read as the card's own type and was readable. */
export function interpretRead(result: DocumentImmediateResult, docTypeHint: string): ReadOutcome {
    if (result.status === DocumentJobStatus.LLM_COMBINED) return { kind: 'several_documents' }
    if (result.status === DocumentJobStatus.LLM_NOT_OF_INTEREST) return { kind: 'wrong_document', cached: result.cached }
    if (result.status !== DocumentJobStatus.LLM_DONE || !result.data) return { kind: 'failed' }
    // A hint the model contradicts is read as the type it saw (a W-9 dropped on the check card): never the card's facts. The server
    // answers a file it read before from that read, whatever card asks, so a file holding two documents reads as the first one again.
    if (result.docType !== docTypeHint) return { kind: 'wrong_document', readAs: result.docType, cached: result.cached }
    if (result.data.isReadable === false) return { kind: 'unreadable' }
    return { kind: 'facts', facts: result.data }
}
